import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { notification, Button, Typography } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import WizardForm from "../components/WizardForm";
import { Wizard } from "../types";
import { mockWizardContent } from "../api/mockData";
import { createWizard } from "../../../services/wizardCmsService";
import { getPublicWizardUrl } from "../utils/url";

const { Title } = Typography;

const CreateWizardPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutate, status } = useMutation<Wizard, Error, Wizard>({
    mutationFn: createWizard,
    onSuccess: (data) => {
      notification.success({
        message: "Wizard Created",
        description: (
          <span>
            The wizard has been created successfully. View it at:{" "}
            <a
              href={getPublicWizardUrl(data.wizardId)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {getPublicWizardUrl(data.wizardId)}
            </a>
          </span>
        ),
      });
      queryClient.invalidateQueries({ queryKey: ["wizards"] });
      navigate(`/wizards/${data.wizardId}/edit`);
    },
    onError: (error: Error) => {
      notification.error({
        message: "Creation Failed",
        description: error.message || "There was an error creating the wizard.",
      });
    },
  });

  const onFinish = (wizardData: Wizard) => {
    mutate(wizardData);
  };

  const newWizard: Wizard = {
    wizardId: "", // Backend should generate this
    name: "New Wizard",
    modifiedAt: new Date().toISOString(),
    content: mockWizardContent,
  };

  return (
    <div style={{ padding: "24px" }}>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/wizards")}
        style={{ marginBottom: 16 }}
      >
        Back to Wizards
      </Button>
      <Title level={2} style={{ marginBottom: 24 }}>
        Create New Wizard
      </Title>
      <WizardForm
        wizard={newWizard}
        onFinish={onFinish}
        isLoading={status === "pending"}
        isCreating={true}
      />
    </div>
  );
};

export default CreateWizardPage;
